import { z } from 'zod';
import { BooleanQuery, PaginationQuerySchema } from './common';
import { StageSituationSchema } from './situations';

/** local-part do alias de abertura por e-mail (`<slug>@...`) — v4 */
export const InboundEmailSlug = z
  .string()
  .regex(/^[a-z0-9](?:[a-z0-9._-]*[a-z0-9])?$/, 'lowercase letters, digits, ".", "_" or "-"');

/** e-mail sem threading vira ticket direto (`direct`) ou pré-ticket p/ triagem (v5) */
export const InboundModeSchema = z.enum(['direct', 'preticket']);
export type InboundMode = z.infer<typeof InboundModeSchema>;

/**
 * Dono dos tickets da mesa (spec 011): `tiflux` = espelhada (o sync escreve, o
 * ERP recusa escrita com 409 ticket_mirrored); `erp` = operada no ERP (o sync
 * não toca). Migrar uma mesa é virar esta chave — e a volta é a mesma chave.
 */
export const DeskOriginSchema = z.enum(['tiflux', 'erp']);
export type DeskOrigin = z.infer<typeof DeskOriginSchema>;

/**
 * Natureza do serviço da mesa (spec 017, FR-003). Até aqui ela era ADIVINHADA
 * pelo nome (`INSTAL`, `MANUTEN`, `RETIR` em serviceOrderTypeFromDeskName, 016)
 * a cada OS gerada; agora é dado da mesa, revisável na tela. `internal` nunca
 * aparece para o cliente (019). A sugestão pelo nome (suggestDeskNature) mora
 * com os presets, em ./profiles: ela conhece nomes de mesa de um cliente.
 */
export const DESK_NATURES = [
  'installation',
  'maintenance',
  'removal',
  'support',
  'other',
  'internal',
] as const;
export const DeskNatureSchema = z.enum(DESK_NATURES);
export type DeskNature = z.infer<typeof DeskNatureSchema>;

export const DeskSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  displayName: z.string(),
  icon: z.string().nullable(),
  active: z.boolean(),
  position: z.number().int(),
  /** alias de abertura por e-mail; null = mesa não recebe e-mail (v4) */
  inboundEmailSlug: z.string().nullable(),
  /** o cliente pode ABRIR ticket nesta mesa pelo portal (v4) */
  portalEnabled: z.boolean(),
  /** o cliente VÊ os tickets desta mesa no portal — padrão fechado (spec 019, US0) */
  clientVisible: z.boolean(),
  /** dispara pesquisa CSAT no fechamento (v4) */
  surveyEnabled: z.boolean(),
  /** destino do e-mail inbound sem threading (v5) */
  inboundMode: InboundModeSchema,
  /**
   * Formulário externo (v5): null = desligado; o VALOR só aparece nas
   * respostas para admin (rotas próprias POST/DELETE /desks/:id/form-token —
   * nunca entra no patch).
   */
  externalFormToken: z.uuid().nullable(),
  /** texto pré-preenchido na descrição de novos tickets da mesa (v5) */
  openingTemplate: z.string().nullable(),
  /** dono dos tickets (spec 011) — muda só por POST /desks/:id/origin (admin) */
  origin: DeskOriginSchema,
  /** natureza do serviço (spec 017) — a OS e o portal leem daqui */
  nature: DeskNatureSchema,
});
export type Desk = z.infer<typeof DeskSchema>;

/**
 * Item de GET /desks: a mesa + quantas etapas estão "a revisar" (situação no
 * padrão Em andamento por falta de sugestão clara) — é o que a lista de mesas
 * mostra para o admin achar o que falta validar com a operação (SC-001).
 */
export const DeskListItemSchema = DeskSchema.extend({
  stagesToReview: z.number().int(),
  /**
   * Spec 031 (D5): o que a regra de disparo da mesa exige do ticket aberto à
   * mão — o telefone sempre; o nome e a placa quando o modelo os usa. null =
   * sem regra ligada, ou com a trava desligada na regra. O "Novo ticket" marca
   * os campos e confere antes do envio, com o mesmo juiz da API.
   */
  dispatchSheet: z
    .object({ phone: z.literal(true), name: z.boolean(), plate: z.boolean() })
    .nullable(),
});
export type DeskListItem = z.infer<typeof DeskListItemSchema>;

export const DeskStageSchema = z.object({
  id: z.number().int(),
  deskId: z.number().int(),
  name: z.string(),
  position: z.number().int(),
  isFirst: z.boolean(),
  isLast: z.boolean(),
  maxTimeMinutes: z.number().int().nullable(),
  /** situação universal da etapa (spec 017) — o ticket herda */
  situation: StageSituationSchema,
  /**
   * false = "a revisar": a situação caiu no padrão Em andamento por falta de
   * sugestão clara pelo nome. Vira true quando alguém salva a etapa.
   */
  situationReviewed: z.boolean(),
});
export type DeskStage = z.infer<typeof DeskStageSchema>;

export const DeskPrioritySchema = z.object({
  id: z.number().int(),
  deskId: z.number().int(),
  name: z.string(),
  position: z.number().int(),
  attendMinutes: z.number().int().nullable(),
  solveMinutes: z.number().int().nullable(),
});
export type DeskPriority = z.infer<typeof DeskPrioritySchema>;

// ── Motivos de fechamento (spec 054) ────────────────────────────────────────
export const DESK_CLOSE_REASON_NAME_MAX = 120;

/**
 * Um motivo de fechamento da mesa. A mesa com pelo menos um ATIVO exige motivo
 * ao fechar; a sem nenhum fecha como sempre. Desativado some da escolha, mas
 * continua nos tickets que já o têm.
 */
export const DeskCloseReasonSchema = z.object({
  id: z.number().int(),
  deskId: z.number().int(),
  name: z.string(),
  active: z.boolean(),
  position: z.number().int(),
});
export type DeskCloseReason = z.infer<typeof DeskCloseReasonSchema>;

export const DeskCloseReasonCreateBodySchema = z
  .object({
    name: z.string().trim().min(1).max(DESK_CLOSE_REASON_NAME_MAX),
    active: z.boolean().default(true),
  })
  .strict();
export type DeskCloseReasonCreateBody = z.infer<typeof DeskCloseReasonCreateBodySchema>;

export const DeskCloseReasonUpdateBodySchema = z
  .object({
    name: z.string().trim().min(1).max(DESK_CLOSE_REASON_NAME_MAX).optional(),
    active: z.boolean().optional(),
  })
  .strict()
  .refine((b) => b.name !== undefined || b.active !== undefined, { message: 'nothing to update' });
export type DeskCloseReasonUpdateBody = z.infer<typeof DeskCloseReasonUpdateBodySchema>;

export const StatusKindSchema = z.enum(['open', 'closed', 'canceled', 'custom']);
export type StatusKind = z.infer<typeof StatusKindSchema>;

export const DeskStatusSchema = z.object({
  id: z.number().int(),
  deskId: z.number().int(),
  name: z.string(),
  kind: StatusKindSchema,
});
export type DeskStatus = z.infer<typeof DeskStatusSchema>;

/** Mesma forma do `ScheduleSlot` de ./business-hours (weekday 0=dom … 6=sáb). */
export const ScheduleSlotSchema = z
  .object({
    weekday: z.number().int().min(0).max(6),
    startMinute: z.number().int().min(0).max(1440),
    endMinute: z.number().int().min(0).max(1440),
  })
  .refine((s) => s.endMinute > s.startMinute, {
    message: 'endMinute must be greater than startMinute',
    path: ['endMinute'],
  });

export const DeskDetailSchema = DeskSchema.extend({
  stages: z.array(DeskStageSchema),
  priorities: z.array(DeskPrioritySchema),
  statuses: z.array(DeskStatusSchema),
  /** expediente semanal da mesa; vazio = 24/7 */
  schedule: z.array(ScheduleSlotSchema),
});
export type DeskDetail = z.infer<typeof DeskDetailSchema>;

export const DeskListQuerySchema = PaginationQuerySchema.extend({
  active: BooleanQuery.optional(),
  search: z.string().optional(),
});
export type DeskListQuery = z.infer<typeof DeskListQuerySchema>;

export const DeskCreateBodySchema = z.object({
  name: z.string().min(1),
  displayName: z.string().min(1).optional(),
  icon: z.string().nullish(),
  /** ausente = sugestão pelo nome (suggestDeskNature) */
  nature: DeskNatureSchema.optional(),
});
export type DeskCreateBody = z.infer<typeof DeskCreateBodySchema>;

export const DeskPatchBodySchema = z
  .object({
    name: z.string().min(1),
    displayName: z.string().min(1),
    icon: z.string().nullable(),
    active: z.boolean(),
    position: z.number().int(),
    inboundEmailSlug: InboundEmailSlug.nullable(),
    portalEnabled: z.boolean(),
    clientVisible: z.boolean(),
    surveyEnabled: z.boolean(),
    inboundMode: InboundModeSchema,
    openingTemplate: z.string().nullable(),
    /** natureza do serviço (spec 017) — configuração do ERP, vale também em mesa espelhada */
    nature: DeskNatureSchema,
    // externalFormToken NÃO entra no patch: gerar/rotacionar/desativar é rota própria
    // origin também não (spec 011): virar a mesa é admin-only e deixa trilha —
    // o .strict() faz um `origin` no patch virar 422 em vez de passar calado
  })
  .partial()
  .strict();
export type DeskPatchBody = z.infer<typeof DeskPatchBodySchema>;

/** POST /desks/:id/origin (admin) — troca o dono dos tickets da mesa. */
export const DeskOriginBodySchema = z.object({ origin: DeskOriginSchema }).strict();
export type DeskOriginBody = z.infer<typeof DeskOriginBodySchema>;

export const StageBodySchema = z.object({
  name: z.string().min(1),
  maxTimeMinutes: z.number().int().positive().nullish(),
  /**
   * Situação (spec 017). Informada = escolha de alguém: a etapa sai de "a
   * revisar". Ausente na criação = sugestão pelo nome; ausente na edição = a
   * situação gravada fica como está (a coluna não tem "vazio").
   */
  situation: StageSituationSchema.optional(),
});
export type StageBody = z.infer<typeof StageBodySchema>;

export const PriorityBodySchema = z.object({
  name: z.string().min(1),
  attendMinutes: z.number().int().positive().nullish(),
  solveMinutes: z.number().int().positive().nullish(),
});
export type PriorityBody = z.infer<typeof PriorityBodySchema>;

export const StatusBodySchema = z.object({
  name: z.string().min(1),
});

export const ReorderBodySchema = z.object({
  /** ids in the desired order */
  ids: z.array(z.number().int().positive()).min(1),
});
export type ReorderBody = z.infer<typeof ReorderBodySchema>;

// ── Expediente + feriados (v3) ──────────────────────────────────────────────
// ScheduleSlotSchema vive acima do DeskDetailSchema (que o embute em `schedule`).

/** PUT /desks/:id/schedule — substitui a semana inteira; vazio = 24/7. */
export const SchedulePutBodySchema = z.object({
  slots: z.array(ScheduleSlotSchema),
});
export type SchedulePutBody = z.infer<typeof SchedulePutBodySchema>;

const DateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');

/** Feriado tenant-wide (vale para todas as mesas). */
export const HolidaySchema = z.object({
  id: z.number().int(),
  date: DateOnly,
  name: z.string(),
});
export type Holiday = z.infer<typeof HolidaySchema>;

export const HolidayBodySchema = z.object({
  date: DateOnly,
  name: z.string().min(1),
});
export type HolidayBody = z.infer<typeof HolidayBodySchema>;
